/**
 * FILE: page-avatar.utils.ts
 * CHỨC NĂNG: Utility lấy URL avatar cho page theo channel
 * TÁC DỤNG: Thống nhất logic lấy avatar page trên toàn bộ codebase
 *
 * Quy tắc:
 * - Nếu có avatarUrl → dùng luôn (xử lý đường dẫn /img/ → /assets/img/)
 * - Facebook (1) / Instagram (2) → Graph API: https://graph.facebook.com/{id}/picture
 * - Các channel khác → logo mặc định theo channel trong assets/img/logo/
 * - Không match → chuỗi rỗng
 */

import {CHANNEL, CHANNEL_PRESENTATIONS} from '@shared/models/conversation/conversation.model';

/** Map channel → logo mặc định (dùng khi page không có avatar URL) */
export const IMAGE_PATH_AVATAR: Partial<Record<number, string>> = Object.fromEntries(
  CHANNEL_PRESENTATIONS
    .filter(channel => channel.id !== CHANNEL.FACEBOOK)
    .map(channel => [channel.id, `/assets/img/${channel.logo}`]),
);

/**
 * Map channel ID → log ảnh mặc định cho page (SHARED với AvatarChannelComponent)
 */
export {IMAGE_PATH_AVATAR as IMAGE_PATH_AVATAR_ALIAS};

/**
 * Lấy URL avatar cho page.
 *
 * @param channel Loại kênh (CHANNEL / CHANNEL value)
 * @param pageId ID page (chỉ cần cho Facebook/Instagram để tạo Graph URL)
 * @param avatarUrl URL avatar có sẵn (từ API) — nếu falsy sẽ dùng logo mặc định
 * @returns URL avatar hoặc chuỗi rỗng
 */
export function getPageAvatar(channel: number, pageId?: string | number, avatarUrl?: string): string {
  const normalizedChannel = Number(channel);

  // Nếu có avatar URL từ API → ưu tiên dùng
  const normalizedAvatarUrl = normalizePageAvatarUrl(normalizedChannel, avatarUrl);
  if (normalizedAvatarUrl) return normalizedAvatarUrl;

  // Facebook / Instagram → lấy từ Graph API
  if (normalizedChannel === CHANNEL.FACEBOOK || normalizedChannel === CHANNEL.INSTAGRAM) {
    if (pageId) {
      return `https://graph.facebook.com/${pageId}/picture?type=small`;
    }
    return '';
  }

  // Các channel còn lại → logo mặc định
  return IMAGE_PATH_AVATAR[normalizedChannel] ?? '';
}

/**
 * Chuẩn hoá URL avatar page trước khi render.
 *
 * Các bản ghi Zalo OA cũ có thể lưu logo Zalo/ZOA qua URL khác nhau. Đây là
 * logo placeholder, không phải avatar page thực tế, nên luôn chuẩn hoá về asset
 * Zalo OA hiện tại. Những avatar khác được giữ nguyên.
 */
export function normalizePageAvatarUrl(channel: number, avatarUrl?: string): string {
  if (!avatarUrl) return '';

  const normalizedUrl = fixAvatarPath(avatarUrl);
  // Một số page WhatsApp/Lazada cũ lưu default_avatar của hệ thống vào DB.
  // Đây không phải avatar channel thật, nên phải tiếp tục fallback về logo kênh.
  if (isDefaultAvatar(normalizedUrl)) {
    return '';
  }

  if (channel === CHANNEL.ZALO && isLegacyZaloOaAvatar(avatarUrl)) {
    return IMAGE_PATH_AVATAR[CHANNEL.ZALO] ?? '/assets/img/logo/zoa.svg';
  }

  return normalizedUrl;
}

/**
 * Fix đường dẫn avatar: nếu db lưu dạng /img/logo/... thì thêm /assets vào trước
 * để trỏ đúng thư mục build assets/img/logo/
 */
export function fixAvatarPath(url: string): string {
  if (url.startsWith('/img/') || url.startsWith('img/')) {
    return '/assets/' + url.replace(/^\//, '');
  }
  return url;
}

function isLegacyZaloOaAvatar(url: string): boolean {
  const pathWithoutQuery = url.split(/[?#]/, 1)[0].toLowerCase();

  return /(?:^|\/)(?:assets\/)?(?:img\/)?logo\/(?:zoa|zalo)\.svg$/.test(pathWithoutQuery);
}

function isDefaultAvatar(url: string): boolean {
  return /(?:^|\/)(?:default[_-]avatar|avatar[_-]default)(?:\.[a-z]+)?(?:[?#]|$)/i.test(url);
}
